"use client";
import { format } from "date-fns-jalali";
import { faIR } from "date-fns-jalali/locale";
import { CalendarDaysIcon } from "lucide-react";
import type { ComponentPropsWithoutRef } from "react";
import type { SelectSingleEventHandler } from "react-day-picker";
import { FloatingLabelButton } from "@/components/ui/buttons/floating-label-button";
import { IconButton } from "@/components/ui/buttons/icon-button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

// ----------------------------------------------------------------------

interface DatePickerProps {
  date?: Date;
  onSelect?: SelectSingleEventHandler;
  label?: string;
  btnProps?: Omit<
    React.ComponentPropsWithoutRef<typeof FloatingLabelButton>,
    "value" | "label"
  >;
  calendarProps?: Omit<
    ComponentPropsWithoutRef<typeof Calendar>,
    "selected" | "onSelect" | "mode"
  >;
  fieldsetClassName?: string;
}

export function DatePicker({
  date,
  onSelect,
  btnProps,
  calendarProps,
  label = "Pick a date",
  fieldsetClassName,
}: DatePickerProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <FloatingLabelButton
          size="sm"
          fieldsetClassName={fieldsetClassName}
          variant="outline"
          label={label}
          value={date ? format(date, "d MMMM yyyy", { locale: faIR }) : ""}
          {...btnProps}
          className={cn(
            "w-full justify-start text-base select-none overflow-x-clip py-1 cursor-text",
            !date && "text-muted-foreground",
            btnProps?.className,
          )}
          endIcon={
            <IconButton size="md" asChild>
              <CalendarDaysIcon className="text-text-disabled stroke-common/80 p-1.5 size-7.5 min-w-7.5 max-w-7.5 min-h-7.5 max-h-7.5 hover:bg-foreground/5" />
            </IconButton>
          }
        />
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <Calendar
          mode="single"
          selected={date}
          onSelect={onSelect}
          {...calendarProps}
        />
      </PopoverContent>
    </Popover>
  );
}
